import { Check, ChevronsUpDown, Home } from 'lucide-react'

import { useAvailableRooms } from '@/entities/reservation/model/useAvailableRooms'
import { cn } from '@/lib/utils'
import { Button } from '@/shared/ui/button'
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from '@/shared/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { HotelReservation } from '@monorepo/functions/src/types/reservations'

type RoomAssignPopoverProps = {
    isOpen: boolean
    setIsOpen: (open: boolean) => void
    selectedRoom?: string
    reservations: HotelReservation[]
    onRoomAssign: (room: string) => void
}

const RoomDisplay = ({ room, pets }: { room: string; pets?: Array<{ name: string; breed: string; sex: string }> }) => (
    <div className='gap-0.25 flex min-w-0 flex-col pl-2'>
        <div className='truncate text-start font-medium'>{room}</div>
        {pets && pets.length > 0 && (
            <div className='truncate text-xs text-muted-foreground'>
                {pets.map(p => `${p.name} (${p.breed}) ${p.sex === 'M' ? '♂️' : '♀️'}`).join(', ')}
            </div>
        )}
    </div>
)

export const RoomAssignPopover = ({
    isOpen,
    setIsOpen,
    selectedRoom,
    reservations,
    onRoomAssign,
}: RoomAssignPopoverProps) => {
    const { availableRooms, currentPetsInRooms } = useAvailableRooms(reservations)

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant='outline'
                    role='combobox'
                    aria-expanded={isOpen}
                    className={cn(
                        'min-h-[2.75rem] w-full justify-between',
                        !selectedRoom ? 'border-amber-300 bg-amber-100 hover:bg-amber-200' : '',
                    )}
                >
                    <div className='flex w-full items-center gap-2'>
                        <Home className={`h-4 w-4 shrink-0 ${!selectedRoom ? 'text-amber-500' : ''}`} />
                        {selectedRoom ? (
                            <RoomDisplay room={selectedRoom} pets={currentPetsInRooms[selectedRoom]} />
                        ) : (
                            <span className='text-amber-700'>Asignar Habitación</span>
                        )}
                    </div>
                    <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
                </Button>
            </PopoverTrigger>
            <PopoverContent className='w-[--radix-popover-trigger-width] p-0'>
                <Command>
                    <CommandInput placeholder='Buscar habitación...' />
                    <CommandEmpty>No se encontraron habitaciones.</CommandEmpty>
                    <CommandList className='max-h-[300px] overflow-auto'>
                        {availableRooms.map(room => (
                            <CommandItem
                                key={room}
                                value={room}
                                onSelect={() => {
                                    onRoomAssign(room)
                                    setIsOpen(false)
                                }}
                            >
                                <Check
                                    className={cn('mr-2 h-4 w-4', selectedRoom === room ? 'opacity-100' : 'opacity-0')}
                                />
                                <RoomDisplay room={room} pets={currentPetsInRooms[room]} />
                            </CommandItem>
                        ))}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
